import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getPortfolioProjects } from '../../lib/api';

const PortfolioSection = () => {
  const { t, language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await getPortfolioProjects();
      setProjects(data);
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    }
  };

  if (projects.length === 0) return null;

  return (
    <section id="portfolio" className="py-20 bg-[#0B1521]" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#4A90D9] text-sm font-semibold tracking-wider uppercase">
            {language === 'fr' ? 'Nos réalisations' : 'Our work'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">
            {language === 'fr' ? 'Portfolio' : 'Portfolio'}
          </h2>
          <p className="text-white/60 mt-4 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Découvrez nos projets et les solutions que nous avons développées pour nos clients.'
              : 'Discover our projects and the solutions we have developed for our clients.'}
          </p>
        </motion.div>

        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden mb-8"
            data-testid={`portfolio-project-${project.id}`}
          >
            <div className="grid md:grid-cols-2 gap-0">
              {/* Images */}
              <div className="relative bg-[#0a0f1a] p-6 flex items-center justify-center min-h-[300px]">
                {project.images && project.images.length > 0 && (
                  <>
                    <motion.img
                      key={activeImage}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      src={project.images[activeImage]}
                      alt={project.name}
                      className="max-h-[350px] rounded-xl object-contain shadow-2xl"
                    />
                    {project.images.length > 1 && (
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                        <button
                          onClick={() => setActiveImage(prev => prev === 0 ? project.images.length - 1 : prev - 1)}
                          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                          data-testid="portfolio-prev-img"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        {project.images.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveImage(i)}
                            className={`w-2.5 h-2.5 rounded-full transition-colors ${i === activeImage ? 'bg-[#4A90D9]' : 'bg-white/30'}`}
                          />
                        ))}
                        <button
                          onClick={() => setActiveImage(prev => prev === project.images.length - 1 ? 0 : prev + 1)}
                          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                          data-testid="portfolio-next-img"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Info */}
              <div className="p-8 flex flex-col justify-center">
                <span className="text-[#4A90D9] text-sm font-medium mb-2">
                  {language === 'fr' ? project.category_fr : project.category_en}
                </span>
                <h3 className="text-2xl font-bold text-white mb-4">{project.name}</h3>
                <p className="text-white/70 leading-relaxed mb-6">
                  {language === 'fr' ? project.description_fr : project.description_en}
                </p>

                {/* Technologies */}
                {project.technologies && project.technologies.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.map((tech) => (
                    <span key={tech} className="px-3 py-1 text-xs font-medium rounded-full bg-[#4A90D9]/10 text-[#4A90D9] border border-[#4A90D9]/20">
                      {tech}
                    </span>
                  ))}
                </div>
                )}

                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4A90D9] text-white rounded-lg font-medium hover:bg-[#5BA0E9] transition-colors w-fit"
                    data-testid="portfolio-link"
                  >
                    {language === 'fr' ? 'Voir le projet' : 'View project'}
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default PortfolioSection;
